import { CrudMode, FormItem, SelectsOptions } from '@ohpen/enso-ui';
import { FieldType, ProcurationFeeItem, ProcurationFeeItemType, FeeUnit } from '@pmp/api';
import type { FormInstance } from 'antd';
import type { FormListFieldData } from 'antd/es/form/FormList';
import { t } from 'i18next';

export const getLenderProcurationFeesFormSchema = (selectsOptions: SelectsOptions, mode?: CrudMode | null): FormItem[] => {
  const baseSchema: FormItem[] = [
    {
      title: 'lender.lender.labels.id',
      dataIndex: 'id',
      formItemProps: {
        hidden: mode === CrudMode.CREATE,
      },
      fieldProps: {
        disabled: true,
      },
    },
    {
      title: 'lender.lender.labels.order',
      dataIndex: 'order',
      tooltip: t('lender.lender.labels.orderInfo'),
      fieldProps: {
        disabled: true,
      },
    },
    {
      title: 'lender.lender.labels.procurationFeeRule',
      dataIndex: 'procurationFeeRuleId',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        optionFilterProp: 'label',
        showSearch: true,
        options: selectsOptions[FieldType.PROCURATION_FEE_RULES],
        disabled: mode === CrudMode.VIEW,
      },
    },
    {
      title: 'lender.lender.labels.procurationFee',
      dataIndex: 'procurationFeeId',
      valueType: 'select',
      formItemProps: {
        required: true,
      },
      fieldProps: {
        optionFilterProp: 'label',
        showSearch: true,
        options: selectsOptions[FieldType.PROCURATION_FEES],
        disabled: mode === CrudMode.VIEW,
      },
    },
  ];

  if (mode !== CrudMode.VIEW) {
    return baseSchema;
  }

  return [
    ...baseSchema,
    {
      title: 'lender.lender.labels.feeIsAbsolute',
      dataIndex: 'feeIsAbsolute',
      valueType: 'select',
      fieldProps: { options: selectsOptions[FieldType.YES_NO], disabled: true },
    },
    {
      dataIndex: 'feeItems',
      valueType: 'formList',
      listProps: {
        creatorButtonProps: false,
        deleteIconProps: false,
      },
      columns: (_meta: FormListFieldData, index: number, form: FormInstance) => {
        const feeItems = (form?.getFieldValue('feeItems') as ProcurationFeeItem[]) ?? [];
        const isCompletion = feeItems[index]?.procurationFeeItemType === ProcurationFeeItemType.COMPLETION;
        const isPercentage = feeItems[index]?.unit === FeeUnit.PERCENTAGE;

        const nonCompletionColumns: FormItem[] = isCompletion
          ? []
          : [
              {
                dataIndex: 'startingMonth',
                title: t('lender.lender.labels.startingMonth'),
                valueType: 'digit' as const,
                colProps: { span: 8 },
                fieldProps: { disabled: true },
              },
              {
                dataIndex: 'intervalInMonths',
                title: t('lender.lender.labels.periodInMonths'),
                valueType: 'digit' as const,
                colProps: { span: 8 },
                fieldProps: { disabled: true },
              },
              {
                dataIndex: 'endingType',
                title: t('lender.lender.labels.endingType'),
                valueType: 'select' as const,
                colProps: { span: 8 },
                fieldProps: { options: selectsOptions[FieldType.ENDING_TYPE], disabled: true },
              },
            ];

        return [
          {
            dataIndex: 'procurationFeeItemType',
            title: t('lender.lender.labels.type'),
            valueType: 'select' as const,
            colProps: { span: 24 },
            fieldProps: { options: selectsOptions[FieldType.PROCURATION_FEE_ITEM_TYPE], disabled: true },
          },
          {
            title: t('lender.lender.labels.value'),
            dataIndex: 'value',
            valueType: isPercentage ? ('percent' as const) : ('digit' as const),
            colProps: { span: 12 },
            fieldProps: { disabled: true, addonBefore: isPercentage ? '%' : '£' },
          },
          {
            dataIndex: 'basis',
            title: t('lender.lender.labels.basis'),
            valueType: 'select' as const,
            colProps: { span: 12 },
            fieldProps: { options: selectsOptions[FieldType.PROCURATION_FEE_BASIS], disabled: true },
          },
          ...nonCompletionColumns,
        ];
      },
    },
  ];
};
